import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function ApplianceRepairsPage() {
  useEffect(() => {
    document.title = 'Electrical Appliance Repairs Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Electrical appliance repairs in Watford. Fix faulty ovens, cookers, showers, heating & kitchen appliances. NICEIC certified electricians. Call 020 3198 1818.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Electrical appliance repairs in Watford. Fix faulty ovens, cookers, showers, heating & kitchen appliances. NICEIC certified electricians. Call 020 3198 1818.';
      document.head.appendChild(meta);
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "url": "https://beyondelectrical.co.uk/services/repairs/appliance-repairs",
      "telephone": "02031981818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "00:00",
        "closes": "23:59"
      },
      "serviceType": "Electrical Appliance Repairs",
      "areaServed": {
        "@type": "City",
        "name": "Watford"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      emoji: '🍳',
      name: 'Electric Cooker Repairs',
      description: 'Repair and maintenance for electric cookers, ovens, and hobs including element replacement and thermostat repairs.'
    },
    {
      emoji: '🚿',
      name: 'Electric Shower Repairs',
      description: 'Fix faulty electric showers including heating element replacement, flow issues, and power supply problems.'
    },
    {
      emoji: '🔥',
      name: 'Immersion Heater Repairs',
      description: 'Repair and replace immersion heaters, thermostats, and associated electrical components for hot water systems.'
    },
    {
      emoji: '🌡️',
      name: 'Storage Heater Repairs',
      description: 'Diagnose and repair storage heaters including element replacement, thermostat repairs, and control upgrades.'
    },
    {
      emoji: '💨',
      name: 'Extractor Fan Repairs',
      description: 'Fix kitchen and bathroom extractor fans including motor replacement, wiring repairs, and timer installations.'
    },
    {
      emoji: '🔌',
      name: 'Appliance Wiring',
      description: 'Install dedicated circuits and repair wiring for high-power appliances ensuring safe and compliant connections.'
    }
  ];

  const commonIssues = [
    {
      emoji: '⚡',
      issue: 'No Power',
      description: 'Appliance not turning on or receiving power, requiring electrical fault diagnosis and repair.'
    },
    {
      emoji: '🔥',
      issue: 'Overheating',
      description: 'Appliances running too hot, tripping circuits, or showing signs of electrical overheating.'
    },
    {
      emoji: '💥',
      issue: 'Tripping Electrics',
      description: 'Appliance causing circuit breakers or RCDs to trip when switched on or during operation.'
    },
    {
      emoji: '⚠️',
      issue: 'Burning Smell',
      description: 'Electrical burning odor from appliances indicating dangerous wiring or component faults.'
    },
    {
      emoji: '🔌',
      issue: 'Faulty Connections',
      description: 'Loose or damaged electrical connections causing intermittent operation or safety hazards.'
    },
    {
      emoji: '🌡️',
      issue: 'Temperature Issues',
      description: 'Appliances not heating properly or maintaining incorrect temperatures due to electrical faults.'
    }
  ];

  const benefits = [
    {
      emoji: '💰',
      title: 'Cost Effective',
      description: 'Professional repairs often cost significantly less than replacing expensive electrical appliances.'
    },
    {
      emoji: '🛡️',
      title: 'Safety Assured',
      description: 'All repairs meet electrical safety standards with proper testing and certification for peace of mind.'
    },
    {
      emoji: '⚡',
      title: 'Expert Diagnosis',
      description: 'Qualified electricians accurately diagnose electrical faults and provide effective repair solutions.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]"></div>
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/c701c194685aed0319cb2f1f903c94d6.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-shield-check-fill"></i>
              NICEIC Approved
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-map-pin-fill"></i>
              Serving Watford
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-tools-fill"></i>
              Expert Repairs
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Electrical Appliance Repairs<br />
            <span className="text-[#FFD700]">in Watford</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Professional electrical appliance repair services in Watford, Rickmansworth, and surrounding areas. From electric cookers and showers to storage heaters and immersion heaters, our NICEIC certified electricians provide expert repairs.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-xl shadow-[#FFD700]/30 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill"></i>
              Get a Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Appliances We Repair
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Expert electrical repairs for all types of household and commercial appliances
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-xl text-3xl mb-4">
                  {service.emoji}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                  {service.name}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Issues */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Common Appliance Problems
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Electrical issues we diagnose and repair in household appliances
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {commonIssues.map((item, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-lg text-2xl mb-4">
                  {item.emoji}
                </div>
                <h3 className="text-lg font-bold text-[#0A1628] mb-2">
                  {item.issue}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Why Repair Instead of Replace?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional appliance repairs offer significant advantages
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg text-center border border-gray-100"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-full text-3xl mx-auto mb-4">
                  {benefit.emoji}
                </div>
                <h3 className="text-2xl font-bold text-[#0A1628] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] to-[#0F2847]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose Beyond Electrical?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Your trusted appliance repair specialists in Watford
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                🔧
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Expert Technicians</h3>
              <p className="text-gray-300 leading-relaxed">
                Qualified electricians with extensive experience repairing all types of electrical appliances
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                ✅
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">NICEIC Certified</h3>
              <p className="text-gray-300 leading-relaxed">
                All repairs meet electrical safety standards with proper testing and certification
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                ⚡
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Fast Service</h3>
              <p className="text-gray-300 leading-relaxed">
                Quick diagnosis and repair to get your appliances working again with minimal downtime
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">350+</div>
              <div className="text-gray-300 text-sm">Appliances Repaired</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">100%</div>
              <div className="text-gray-300 text-sm">Safety Compliant</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">24/7</div>
              <div className="text-gray-300 text-sm">Emergency Service</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">15+</div>
              <div className="text-gray-300 text-sm">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-6">
            Areas We Cover
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Professional appliance repair services throughout Watford, Rickmansworth, and surrounding areas including:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {['Watford', 'Rickmansworth', 'Bushey', 'Croxley Green', 'Abbots Langley', 'Kings Langley', 'Chorleywood', 'Northwood', 'Pinner', 'Harrow', 'Borehamwood', 'Radlett'].map((area) => (
              <span
                key={area}
                className="bg-gradient-to-r from-[#FFD700] to-[#FFC700] text-[#0A1628] px-5 py-2.5 rounded-full font-semibold text-sm shadow-md"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Need Appliance Repairs?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-10 leading-relaxed">
            Contact us today for expert electrical appliance repairs in Watford. Our qualified electricians can diagnose and fix most appliance issues quickly and safely.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-10 py-5 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-2xl shadow-[#FFD700]/40 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xl"></i>
              Call 020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill text-xl"></i>
              Request a Quote
            </a>
          </div>
          <div className="mt-8 flex items-center justify-center gap-2 text-[#FFD700] text-sm font-semibold">
            <i className="ri-tools-fill"></i>
            Expert Diagnosis &amp; Repair
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}